import { Search } from 'lucide-react';
import { useMemo, useState } from 'react';
import { toast } from 'sonner';

import {
  type TagBulkChargeFamilyPreview,
  useResolveTagBulkChargeAudience,
} from '~entities/payment/api/bulk-charge-by-tags';
import { useGetTags } from '~entities/tags/api/get-groups';
import { useGetV2ModeStatus } from '~entities/v2-mode/api/v2-mode-api';

import { Button } from '~shared/shadcn/ui/button';
import { Checkbox } from '~shared/shadcn/ui/checkbox';
import { Input } from '~shared/shadcn/ui/input';
import { Label } from '~shared/shadcn/ui/label';
import { ScrollArea } from '~shared/shadcn/ui/scroll-area';
import { capitalizeWords } from '~shared/helpers/text-formatting';
import useDebounce from '~shared/hooks/useDebounce';

export interface SelectByTagTabProps {
  onFamiliesResolved: (families: Array<TagBulkChargeFamilyPreview>) => void;
}

export function SelectByTagTab({ onFamiliesResolved }: SelectByTagTabProps) {
  const [search, setSearch] = useState('');
  const debouncedSearch = useDebounce(search, 300);
  const [selectedContactTagIds, setSelectedContactTagIds] = useState<
    Array<string>
  >([]);
  const [selectedEnrollmentTagIds, setSelectedEnrollmentTagIds] = useState<
    Array<string>
  >([]);

  const { data: tagsData, isFetching } = useGetTags({
    page: 1,
    // Bulk charge needs the full studio tag catalog; 50 hid most CRM/portal tags.
    limit: 1000,
    search: debouncedSearch,
  });

  const resolveMutation = useResolveTagBulkChargeAudience();
  const { data: v2ModeStatus } = useGetV2ModeStatus();
  const isV2 = v2ModeStatus?.isV2 === true;

  const tagOptions = useMemo(() => {
    const tags = (tagsData?.items ?? []).map((tag) => ({
      id: String(tag._id),
      name: capitalizeWords(tag.fieldName),
    }));

    // One list: each tag appears as CRM and Class match sources.
    return tags
      .flatMap((tag) => [
        { ...tag, source: 'contact' as const, sourceLabel: 'CRM' },
        { ...tag, source: 'enrollment' as const, sourceLabel: 'Class' },
      ])
      .sort((a, b) => {
        const nameCompare = a.name.localeCompare(b.name, undefined, {
          sensitivity: 'base',
        });
        if (nameCompare !== 0) {
          return nameCompare;
        }
        // Prefer CRM before Class for the same tag name.
        if (a.sourceLabel === b.sourceLabel) {
          return 0;
        }
        return a.sourceLabel === 'CRM' ? -1 : 1;
      });
  }, [tagsData?.items]);

  const toggleTag = (
    source: 'contact' | 'enrollment',
    tagId: string,
    checked: boolean,
  ) => {
    const setter =
      source === 'contact'
        ? setSelectedContactTagIds
        : setSelectedEnrollmentTagIds;
    setter((prev) =>
      checked ? [...new Set([...prev, tagId])] : prev.filter((id) => id !== tagId),
    );
  };

  const isTagSelected = (source: 'contact' | 'enrollment', tagId: string) =>
    source === 'contact'
      ? selectedContactTagIds.includes(tagId)
      : selectedEnrollmentTagIds.includes(tagId);

  const handlePreview = () => {
    if (
      selectedContactTagIds.length === 0 &&
      selectedEnrollmentTagIds.length === 0
    ) {
      toast.error('Select at least one CRM or Class tag');
      return;
    }

    resolveMutation.mutate(
      {
        params: {
          contactTagIds: selectedContactTagIds,
          enrollmentTagIds: selectedEnrollmentTagIds,
        },
        isV2,
      },
      {
        onSuccess: (data) => {
          if (!data.families.length) {
            toast.message('No families matched the selected tags');
            return;
          }
          onFamiliesResolved(data.families);
        },
        onError: () => {
          toast.error('Failed to resolve tagged families');
        },
      },
    );
  };

  return (
    <div className='space-y-4'>
      <p className='text-sm text-muted-foreground'>
        Charge once per family. Pick tags by CRM or Class match — sources stay
        labeled in the preview.
      </p>

      <div className='relative'>
        <Search className='absolute left-2 top-2.5 size-4 text-muted-foreground' />
        <Input
          id='tag-search'
          placeholder='Search tags...'
          className='pl-8'
          value={search}
          onChange={(e) => {
            setSearch(e.target.value);
          }}
        />
      </div>

      <div className='flex flex-col space-y-3'>
        <div className='flex items-center justify-between px-1'>
          <h3 className='text-sm font-medium text-slate-700'>Tags</h3>
          {tagsData?.pagination?.total != null ? (
            <span className='text-xs text-muted-foreground'>
              {tagsData.pagination.total} tag
              {tagsData.pagination.total === 1 ? '' : 's'}
            </span>
          ) : null}
        </div>

        <ScrollArea className='h-[220px] rounded-md border'>
          <div className='p-2'>
            {tagOptions.length === 0 && !isFetching ? (
              <p className='py-4 text-center text-muted-foreground'>
                No tags found
              </p>
            ) : (
              tagOptions.map((tag) => (
                <Label
                  key={`${tag.source}-${tag.id}`}
                  className='flex cursor-pointer items-center space-x-3 rounded-md px-2 py-1.5 text-sm font-normal hover:bg-muted'
                >
                  <Checkbox
                    checked={isTagSelected(tag.source, tag.id)}
                    onCheckedChange={(checked) => {
                      toggleTag(tag.source, tag.id, checked === true);
                    }}
                    className='size-4'
                  />
                  <span className='text-sm font-medium leading-none'>
                    {tag.name} ({tag.sourceLabel})
                  </span>
                </Label>
              ))
            )}
          </div>
        </ScrollArea>
      </div>

      <div className='flex justify-end'>
        <Button
          onClick={handlePreview}
          disabled={resolveMutation.isPending}
        >
          {resolveMutation.isPending
            ? 'Resolving...'
            : 'Preview families'}
        </Button>
      </div>
    </div>
  );
}
